import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';

import { backgroundStyle } from '@/entities/appearance';
import {
  type BoardDetail,
  calendarColumnCardCount,
  type CardFilter,
  EMPTY_FILTER,
  isFilterActive,
  readVisibility,
  scopeKey,
  useBoardCardSearch,
  useBoardDetail,
  useLinkPreviewPolling,
  useSetCalendarColumnVisible,
  recordRecentBoard,
} from '@/entities/board';
import { useBoardRealtime } from '@/entities/realtime';
import { useWorkspaces } from '@/entities/workspace';
import { ActivityDrawer } from '@/features/board-activity';
import { AccessDialog } from '@/features/board-access';
import { BoardFilterBar, filterFromParams, filterToParams } from '@/features/board-filter';
import { BoardAppearanceDialog, CreateBoardDialog } from '@/features/board-manage';
import { RecoveryDock, type RecoveryKind } from '@/features/board-recovery';
import { BoardViewDock } from '@/features/board-view-dock';
import { AdvancedCreateDialog } from '@/features/advanced-create';
import { QuickCreateDialog } from '@/features/quick-create';
import {
  CalendarView,
  clearCalendarState,
  defaultCalendarView,
  isCalendarOpen,
  writeCalendarState,
} from '@/features/calendar';
import { TimelineDrawer } from '@/features/timeline';
import { LabelManager } from '@/features/label-manager';
import { BoardCanvas } from '@/features/kanban';
import { useViewer } from '@/entities/viewer';
import { ExportBoardDialog } from '@/features/board-portability';
import { isApiError } from '@/shared/api';
import { todayIn } from '@/shared/lib/calendar';
import { useIsMobile, useIsShortLandscape } from '@/shared/lib/media';
import { originStateFrom } from '@/shared/lib/origin';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import { useShortcutHandler } from '@/shared/lib/shortcuts';
import { Dialog, ErrorState, Icons, LoadingState, MenuItem, MenuList, MenuRow } from '@/shared/ui';

import { BoardHeader } from './BoardHeader';
import { BoardSettingsDialog } from './BoardSettingsDialog';
import styles from './BoardPage.module.css';

/** Outlet context for the nested card route. */
export type BoardOutletContext = { detail: BoardDetail };

export function BoardPage() {
  const { t, i18n } = useTranslation();
  const viewer = useViewer();
  const navigate = useNavigate();
  const location = useLocation();
  const { boardId = '' } = useParams();
  const query = useBoardDetail(boardId);
  useLinkPreviewPolling(boardId, query.data);
  // Real-time hints for this board (the server re-checks access on subscribe and on changes).
  useBoardRealtime(boardId);
  const workspaceList = useWorkspaces();
  const viewerId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const loadedBoard = query.isSuccess;
  // Only a board that really loaded (so the user may see it) counts as "recently opened".
  useEffect(() => {
    if (viewerId && loadedBoard && boardId) recordRecentBoard(viewerId, boardId);
  }, [viewerId, loadedBoard, boardId]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [newBoardOpen, setNewBoardOpen] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [advancedCreateOpen, setAdvancedCreateOpen] = useState(false);
  // One side view at a time (Timeline or Activity), both opened from the bottom dock.
  const [view, setView] = useState<'timeline' | 'activity' | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [recovery, setRecovery] = useState<RecoveryKind | null>(null);
  const mobile = useIsMobile();
  // A phone on its side has the width of a desktop but not its height: the header takes the
  // compact composition (create + menu, everything else behind the menu).
  const shortLandscape = useIsShortLandscape();
  const compactHeader = mobile || shortLandscape;
  const dock = useRef<HTMLElement>(null);
  // Filter state lives in the URL: survives reload, shareable, back/forward aware.
  const [searchParams, setSearchParams] = useSearchParams();
  const [accessOpen, setAccessOpen] = useState(searchParams.get('access') !== null);
  const filter = useMemo(() => filterFromParams(searchParams), [searchParams]);
  const filtering = isFilterActive(filter);
  // The Calendar replaces the Kanban canvas; its state (view, date, filters) lives in the URL.
  const calendarOpen = isCalendarOpen(searchParams);
  const user = viewer.status === 'authenticated' ? viewer.user : null;
  const toggleCalendar = () => {
    setView(null);
    // The four switches come from this board's remembered preference (the URL stays clean).
    const storageKey = scopeKey({ view: 'calendar', kind: 'board', id: boardId });
    const stored = readVisibility(storageKey);
    // Opens in the view last chosen on this kind of device (a phone starts on its day).
    const view = defaultCalendarView(storageKey, mobile ? 'phone' : 'desktop');
    setSearchParams(
      calendarOpen
        ? clearCalendarState(searchParams)
        : writeCalendarState(
            searchParams,
            {
              mode: view.mode,
              density: view.density,
              date: todayIn(user?.timezone),
              showTasks: stored.showTasks,
              showEvents: stored.showEvents,
              includeCompleted: stored.showCompleted,
              includeOverdue: stored.showOverdue,
              search: '',
              labelIds: [],
              hiddenBoardIds: [],
            },
            stored,
            view,
          ),
    );
  };
  const search = useBoardCardSearch(boardId, filter, filtering);
  const setCalendarColumn = useSetCalendarColumnVisible(boardId);
  const visibleCardIds = useMemo(
    // Until the server's answer is known there is no result set: the board is not narrowed to
    // an empty one (that would flash every column as "no matches").
    () => (filtering && search.data ? new Set(search.data.map((card) => card.id)) : null),
    [filtering, search.data],
  );
  // react-router's functional setSearchParams reads the params of the last
  // render, so quick consecutive updates would overwrite each other: keep the
  // not-yet-committed filter here until the navigation lands.
  const pendingFilter = useRef<CardFilter | null>(null);
  useEffect(() => {
    pendingFilter.current = null;
  }, [searchParams]);
  const setFilter = useCallback(
    (update: (current: CardFilter) => CardFilter) => {
      const next = update(pendingFilter.current ?? filter);
      pendingFilter.current = next;
      setSearchParams(filterToParams(next), { replace: true });
    },
    [filter, setSearchParams],
  );

  const permissions = query.data?.permissions;
  const mayCreate = can(permissions, PERMISSIONS.taskCreate);
  useShortcutHandler(
    'new-task',
    mayCreate
      ? () => {
          setQuickCreateOpen(true);
        }
      : null,
  );
  useShortcutHandler('search', () => {
    document.querySelector<HTMLInputElement>('[role="search"] input')?.focus();
  });

  if (query.isPending) return <LoadingState />;
  if (query.isError) {
    // 404 for a board that was open: access was withdrawn (or the board is gone). Say so
    // instead of offering a retry that cannot succeed.
    if (isApiError(query.error) && query.error.status === 404) {
      return (
        <div className={styles.status}>
          <div role="alert">
            <p>{t('boardHeader.accessGone')}</p>
          </div>
          <Link to="/app">{t('common.backHome')}</Link>
        </div>
      );
    }
    return (
      <div className={styles.status}>
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        <Link to="/app">{t('common.backHome')}</Link>
      </div>
    );
  }

  const detail = query.data;
  const { board } = detail;
  const openCard = (cardId: string) =>
    void navigate({ pathname: `cards/${cardId}`, search: searchParams.toString() });
  // Reflection of the server's decision (it enforces every action).
  const canEdit = can(detail.permissions, PERMISSIONS.boardEditDetails);
  const canCreate = can(detail.permissions, PERMISSIONS.taskCreate);
  const canScheduleCards = can(detail.permissions, PERMISSIONS.taskEditSchedule);
  // A board-only guest is not a member: the parent workspace is not theirs to open.
  const isMember = (workspaceList.data ?? []).some((w) => w.id === board.workspace_id);
  const workspacePath = isMember ? `/app/workspaces/${board.workspace_id}` : '/app';
  const calendarColumnVisible = detail.show_calendar_column;
  const calendarCount = calendarColumnCardCount(detail);
  const toggleCalendarColumn = () => {
    setCalendarColumn.mutate(!calendarColumnVisible);
  };
  // The board's own background (the page behind the columns); the semantic color is separate.
  const background = backgroundStyle(board.appearance?.background, board.background_image_url);
  const hasBackground = Object.keys(background.style).length > 0;

  // The board views: a floating dock, or (on a phone on its side) a row of icons in the header.
  const dockElement = (inline: boolean) => (
    <BoardViewDock
      ref={dock}
      inline={inline}
      views={[
        {
          id: 'timeline',
          label: t('timeline.open'),
          icon: '🕑',
          active: !calendarOpen && view === 'timeline',
          onSelect: () => {
            if (calendarOpen) setSearchParams(clearCalendarState(searchParams));
            setView((current) => (current === 'timeline' && !calendarOpen ? null : 'timeline'));
          },
        },
        {
          id: 'activity',
          label: t('dock.activity'),
          icon: '🗒',
          active: !calendarOpen && view === 'activity',
          onSelect: () => {
            if (calendarOpen) setSearchParams(clearCalendarState(searchParams));
            setView((current) => (current === 'activity' && !calendarOpen ? null : 'activity'));
          },
        },
        {
          id: 'calendar',
          label: t('calendar.dock'),
          icon: '📅',
          active: calendarOpen,
          onSelect: toggleCalendar,
        },
      ]}
    />
  );

  return (
    <div
      className={`${styles.page} ${hasBackground ? 'board-has-bg' : ''}`}
      data-bg={hasBackground ? 'true' : undefined}
      data-columns-only={
        !calendarOpen && view !== 'timeline' && view !== 'activity' ? 'true' : undefined
      }
      style={background.style}
    >
      <BoardHeader
        board={board}
        canEdit={canEdit}
        canCreate={canCreate}
        mobile={compactHeader}
        {...(shortLandscape && { viewSwitcher: dockElement(true) })}
        workspacePath={workspacePath}
        onQuickCreate={() => {
          setQuickCreateOpen(true);
        }}
        onAdvancedCreate={() => {
          setAdvancedCreateOpen(true);
        }}
        onLabels={() => {
          setLabelsOpen(true);
        }}
        onAppearance={() => {
          setAppearanceOpen(true);
        }}
        onExport={() => {
          setExportOpen(true);
        }}
        onSettings={() => {
          setSettingsOpen(true);
        }}
        onMenu={() => {
          setMenuOpen(true);
        }}
        onNewBoard={() => {
          setNewBoardOpen(true);
        }}
        onAccess={() => {
          setAccessOpen(true);
        }}
      />

      <div className={styles.work}>
        {!calendarOpen && view === 'timeline' && (
          <TimelineDrawer
            detail={detail}
            onOpenCard={openCard}
            onClose={() => {
              setView(null);
              dock.current?.querySelector<HTMLElement>('[data-view="timeline"]')?.focus();
            }}
          />
        )}
        {!calendarOpen && view === 'activity' && (
          <ActivityDrawer
            boardId={board.id}
            onOpenCard={openCard}
            onClose={() => {
              setView(null);
              dock.current?.querySelector<HTMLElement>('[data-view="activity"]')?.focus();
            }}
          />
        )}
        {calendarOpen && (
          <div className={styles.main}>
            <CalendarView
              scope={{ kind: 'board', boardId: board.id }}
              boards={[board]}
              canEdit={canScheduleCards}
              firstDay={user?.first_day_of_week ?? 0}
              timeZone={user?.timezone ?? null}
              locale={i18n.language}
              workweekDays={board.workweek_days}
              onOpenCard={openCard}
            />
          </div>
        )}
        {!calendarOpen && (
          <div className={styles.main}>
            <BoardFilterBar
              labels={detail.labels}
              filter={filter}
              onChange={setFilter}
              matches={search.data ? search.data.length : null}
            />
            {search.isError && (
              <ErrorState error={search.error} onRetry={() => void search.refetch()} />
            )}

            <BoardCanvas
              detail={detail}
              visibleCardIds={visibleCardIds}
              // Columns are hidden only once the result is known (no flash of an empty board).
              filterActive={filtering && search.data !== undefined}
              filterPending={filtering && search.data === undefined}
              onResetFilters={() => {
                setFilter(() => EMPTY_FILTER);
              }}
              onOpenCard={openCard}
            />
          </div>
        )}
      </div>

      {labelsOpen && (
        <LabelManager
          boardId={board.id}
          labels={detail.labels}
          onClose={() => {
            setLabelsOpen(false);
          }}
        />
      )}
      {!shortLandscape && dockElement(false)}
      <RecoveryDock
        boardId={board.id}
        canDeleteTasks={can(detail.permissions, PERMISSIONS.taskDelete)}
        open={recovery}
        onOpenChange={setRecovery}
        showButtons={!compactHeader}
        calendarColumn={
          calendarOpen
            ? undefined
            : {
                visible: calendarColumnVisible,
                count: calendarCount,
                onToggle: toggleCalendarColumn,
              }
        }
      />
      {menuOpen && (
        <Dialog
          title={t('boards.menuTitle')}
          onClose={() => {
            setMenuOpen(false);
          }}
        >
          <MenuList>
            {(
              [
                [
                  'advancedCreate.open',
                  <Icons.edit key="a" />,
                  () => {
                    setAdvancedCreateOpen(true);
                  },
                ],
                [
                  'boards.settings',
                  <Icons.sliders key="s" />,
                  () => {
                    setSettingsOpen(true);
                  },
                ],
                [
                  'boards.appearance',
                  <Icons.palette key="p" />,
                  () => {
                    setAppearanceOpen(true);
                  },
                ],
                [
                  'access.settings',
                  <Icons.key key="u" />,
                  () => {
                    setAccessOpen(true);
                  },
                ],
                [
                  'portability.export.open',
                  <Icons.export key="d" />,
                  () => {
                    setExportOpen(true);
                  },
                ],
                [
                  'labels.manage',
                  <Icons.tag key="t" />,
                  () => {
                    setLabelsOpen(true);
                  },
                ],
                [
                  calendarColumnVisible ? 'calendarColumn.hide' : 'calendarColumn.show',
                  <Icons.calendar key="c" />,
                  toggleCalendarColumn,
                ],
                [
                  'archive.open',
                  <Icons.archive key="r" />,
                  () => {
                    setRecovery('archive');
                  },
                ],
                [
                  'trash.open',
                  <Icons.trash key="x" />,
                  () => {
                    setRecovery('trash');
                  },
                ],
              ] as const
            ).map(([label, icon, action]) => (
              <MenuRow key={label}>
                <MenuItem
                  icon={icon}
                  onClick={() => {
                    setMenuOpen(false);
                    action();
                  }}
                >
                  {t(label)}
                </MenuItem>
              </MenuRow>
            ))}
          </MenuList>
        </Dialog>
      )}

      {newBoardOpen && board.workspace_id !== null && (
        <CreateBoardDialog
          workspaceId={board.workspace_id}
          onCreated={(created) => {
            setNewBoardOpen(false);
            void navigate(`/app/boards/${created.id}`, { state: originStateFrom(location) });
          }}
          onClose={() => {
            setNewBoardOpen(false);
          }}
        />
      )}
      {quickCreateOpen && (
        <QuickCreateDialog
          workspaceId={board.workspace_id}
          fixedBoardId={board.id}
          timezone={user?.timezone ?? null}
          onClose={() => {
            setQuickCreateOpen(false);
          }}
        />
      )}
      {advancedCreateOpen && (
        <AdvancedCreateDialog
          workspaceId={board.workspace_id}
          fixedBoardId={board.id}
          timezone={user?.timezone ?? null}
          onClose={() => {
            setAdvancedCreateOpen(false);
          }}
        />
      )}
      {exportOpen && (
        <ExportBoardDialog
          boardId={board.id}
          onClose={() => {
            setExportOpen(false);
          }}
        />
      )}
      {appearanceOpen && (
        <BoardAppearanceDialog
          key={board.version}
          board={board}
          onClose={() => {
            setAppearanceOpen(false);
          }}
        />
      )}
      {settingsOpen && (
        <BoardSettingsDialog
          key={board.version}
          board={board}
          columns={detail.columns}
          role={detail.role}
          permissions={detail.permissions}
          onClose={() => {
            setSettingsOpen(false);
          }}
          onDeleted={() => void navigate(workspacePath, { replace: true })}
          onAppearance={() => {
            setAppearanceOpen(true);
          }}
          onExport={() => {
            setExportOpen(true);
          }}
        />
      )}
      {accessOpen && (
        <AccessDialog
          board={board}
          role={detail.role}
          permissions={detail.permissions}
          initialTab={searchParams.get('access') === 'requests' ? 'requests' : 'people'}
          onClose={() => {
            setAccessOpen(false);
          }}
        />
      )}
      <Outlet context={{ detail } satisfies BoardOutletContext} />
    </div>
  );
}
